/**
 * Which add-ons apply to which wrap.
 *
 * Source: the client's "Recovery Packages Mapping" sheet, tab GAME READY RECOVERY
 * PACKAGES (received 2026-08-17). Their feedback was that every add-on currently
 * shows on every order regardless of the body part being treated.
 *
 * Only the Freeze Sleeve rule is enforced here, because it is the only one that can
 * be: the ZAMST, Proven Recovery and Stopain products in that sheet **do not exist in
 * Shopify yet**. The catalogue holds exactly two add-on products today — "Freeze
 * Sleeve" and "Freeze Sleeve Flat Pak". Once the others are created, extend
 * WRAP_ADDON_RULES rather than adding a second mechanism.
 *
 * The sheet's rule is per wrap: either the sized Freeze Sleeve ("ALL except Flat Pak")
 * or the Flat Pak ("Flat Pak ONLY") — never both.
 */

export type FreezeSleeveScope = 'sleeve_only' | 'flatpak_only';

/**
 * Wrap SKU → rule. SKUs are the live catalogue values seen on migrated orders; the
 * sheet names them in prose ("STRAIGHT KNEE WRAP"), so this is the join between the
 * two. A wrap absent from this table is left unfiltered — showing too much is a far
 * smaller problem than silently hiding an add-on the operator needs.
 */
export const WRAP_ADDON_RULES: Record<string, { freezeSleeve: FreezeSleeveScope }> = {
  'knee-wrap-straight-10': { freezeSleeve: 'sleeve_only' },   // STRAIGHT KNEE WRAP
  'hip-groin-wrap-10': { freezeSleeve: 'flatpak_only' },      // HIP/GROIN WRAP
  'shoulder-wrap-10': { freezeSleeve: 'flatpak_only' },       // SHOULDER WRAP
  'ankle-wrap-12': { freezeSleeve: 'sleeve_only' },           // ANKLE WRAP
  'half-leg-boot-wrap-12': { freezeSleeve: 'sleeve_only' },   // HALF LEG BOOT WRAP
  'hand-wrist-wrap-10': { freezeSleeve: 'sleeve_only' },      // HAND/WRIST WRAP
  'elbow-wrap-straight-10': { freezeSleeve: 'sleeve_only' },  // STRAIGHT ELBOW WRAP
  'elbow-wrap-flexed-10': { freezeSleeve: 'sleeve_only' },    // FLEXED ELBOW WRAP
  'back-wrap-12': { freezeSleeve: 'flatpak_only' },           // BACK WRAP
  'c-t-spine-wrap-10': { freezeSleeve: 'flatpak_only' },      // CT SPINE WRAP
};

/** Identifies the Flat Pak product; everything else Freeze-Sleeve is the sized sleeve. */
const isFlatPak = (title: string) => /flat\s*pak/i.test(title);
const isFreezeSleeveFamily = (title: string) => /freeze\s*sleeve/i.test(title);

/**
 * Narrow the add-on list to what the chosen wrap allows.
 *
 * Returns the list unchanged when no wrap is chosen yet, or when the wrap has no rule
 * on file — deliberately permissive, since the cost of hiding a valid add-on is worse
 * than showing an extra one.
 */
export function addOnsForWrap<T extends { title: string }>(
  addons: T[],
  wrapSku: string | null | undefined,
): T[] {
  if (!wrapSku) return addons;
  const rule = WRAP_ADDON_RULES[wrapSku];
  if (!rule) return addons;

  return addons.filter((a) => {
    if (!isFreezeSleeveFamily(a.title)) return true; // not governed by this rule
    return rule.freezeSleeve === 'flatpak_only' ? isFlatPak(a.title) : !isFlatPak(a.title);
  });
}

/** Human-readable reason, shown so the operator knows why the list is shorter. */
export function wrapAddOnNote(wrapSku: string | null | undefined): string | null {
  if (!wrapSku) return null;
  const rule = WRAP_ADDON_RULES[wrapSku];
  if (!rule) return null;
  return rule.freezeSleeve === 'flatpak_only'
    ? 'This wrap takes the Freeze Sleeve Flat Pak.'
    : 'This wrap takes the sized Freeze Sleeve.';
}
